"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/shadcn/ui/input";
import { CornerButton } from "@/shadcn/ui/corner-button";

// Register schema with password
const registerSchema = z
  .object({
    email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
    password: z.string().min(6, "Das Passwort muss mindestens 6 Zeichen lang sein"),
    confirmPassword: z.string().min(6, "Bitte bestätigen Sie das Passwort"),
    firstName: z.string().min(2, "Der Vorname muss mindestens 2 Zeichen lang sein"),
    lastName: z.string().min(2, "Der Nachname muss mindestens 2 Zeichen lang sein"),
    acceptsMarketing: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Die Passwörter stimmen nicht überein",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

interface SignUpFormProps {
  onSuccess?: () => void;
}

export default function SignUpForm({ onSuccess }: SignUpFormProps) {
  const { register, isLoading } = useAuth();

  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptsMarketing: true,
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    const response = await register({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
      acceptsMarketing: data.acceptsMarketing || false,
    });

    // If registration was successful, call onSuccess callback
    if (response.success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <form
      onSubmit={registerForm.handleSubmit(onSubmit)}
      className="flex flex-col gap-[1.5rem]"
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="firstName"
            className="block text-[1.75rem] text-black/80 mb-2"
          >
            Vorname
          </label>
          <Input
            type="text"
            id="firstName"
            {...registerForm.register("firstName")}
            placeholder="Max"
            className={cn(
              "rounded-[.75rem] border border-black/20 !text-black placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
              registerForm.formState.errors.firstName && "!border-red-500",
            )}
          />
          {registerForm.formState.errors.firstName && (
            <p className="mt-1 text-sm text-red-600 mb-0">
              {registerForm.formState.errors.firstName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="block text-[1.75rem] text-black/80 mb-2"
          >
            Nachname
          </label>
          <Input
            type="text"
            id="lastName"
            {...registerForm.register("lastName")}
            placeholder="Mustermann"
            className={cn(
              "rounded-[.75rem] border border-black/20 !text-black placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
              registerForm.formState.errors.lastName && "!border-red-500",
            )}
          />
          {registerForm.formState.errors.lastName && (
            <p className="mt-1 text-sm text-red-600 mb-0">
              {registerForm.formState.errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="registerEmail"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          E-Mail-Adresse
        </label>
        <Input
          type="email"
          id="registerEmail"
          {...registerForm.register("email")}
          placeholder="your@email.com"
          className={cn(
            "rounded-[.75rem] border border-black/20 !text-black placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
            registerForm.formState.errors.email && "!border-red-500",
          )}
        />
        {registerForm.formState.errors.email && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {registerForm.formState.errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="password"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          Passwort
        </label>
        <Input
          type="password"
          id="password"
          {...registerForm.register("password")}
          placeholder="Passwort erstellen"
          className={cn(
            "rounded-[.75rem] border border-black/20 !text-black placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
            registerForm.formState.errors.password && "!border-red-500",
          )}
        />
        {registerForm.formState.errors.password && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {registerForm.formState.errors.password.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="confirmPassword"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          Passwort bestätigen
        </label>
        <Input
          type="password"
          id="confirmPassword"
          {...registerForm.register("confirmPassword")}
          placeholder="Bestätigen Sie Ihr Passwort"
          className={cn(
            "rounded-[.75rem] border border-black/20 !text-black    placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
            registerForm.formState.errors.confirmPassword && "!border-red-500",
          )}
        />
        {registerForm.formState.errors.confirmPassword && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {registerForm.formState.errors.confirmPassword.message}
          </p>
        )}
      </div>

      <div className="mb-[2rem] mt-[1rem]">
        <div className="flex items-center gap-[1rem]">
          <input
            type="checkbox"
            id="acceptsMarketing"
            {...registerForm.register("acceptsMarketing")}
            className={cn(
              "h-[2.5rem] w-[2.5rem] text-black focus:ring-black border-gray-300 rounded cursor-pointer transition-all",
              registerForm.formState.errors.acceptsMarketing &&
                "!border-red-500",
            )}
          />
          <label htmlFor="acceptsMarketing" className="text-black/80 cursor-pointer">
            Ich möchte Marketing-E-Mails und Neuigkeiten erhalten
          </label>
        </div>

        {registerForm.formState.errors.acceptsMarketing && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {registerForm.formState.errors.acceptsMarketing.message}
          </p>
        )}
      </div>

      <CornerButton
        className="[clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] !bg-black w-full"
        type="dark"
        htmlType="submit"
        disabled={isLoading}
      >
        <div className="corner-button-text relative">
          <div className="corner-button-text-initial transition-all duration-300">
            {isLoading ? "Konto wird erstellt…" : "Konto erstellen"}
          </div>
          <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
            {isLoading ? "Konto wird erstellt…" : "Konto erstellen"}
          </div>
        </div>
      </CornerButton>
    </form>
  );
}
